import React from 'react';
import {Form as FormikForm, Formik, FormikHelpers, FormikValues} from 'formik';
import * as Yup from 'yup';
import PrimaryButton from '../../ui/PrimaryButton';
import TextButton from '../../ui/TextButton';
import UIInput from '../../ui/form/UIInput';
import FormSection from '../../ui/form/FormSection';
import FormField from '../../ui/form/FormField';
import {LicenseType} from "../../schema";
import UIDatePeriod from "../../ui/form/UIDatePeriod";
import gql from "graphql-tag";
import {useQuery} from "@apollo/client";
import UISelect from "../../ui/form/UISelect";
import UIWysiwig from "../../ui/form/UIWysiwig";
import Countries from "../../models/utils/Countries";
import UIPrice from "../../ui/form/UIPrice";

type FormProps = {
  backLink: string;
  onSubmit?: (input: any) => void;
}

export default function CompanyBrandAddForm({backLink, onSubmit}: FormProps) {
  const onFormSubmit = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    helpersReact.setSubmitting(true);
    if (onSubmit) {
      onSubmit(values);
      helpersReact.setSubmitting(false);
    }
  };

  const {loading, data} = useQuery<FormDataQuery>(query);

  if (loading && !data) {
    return <div>Loading...</div>;
  }

  const initialValues = {
    name: '',
    country: 'AF',
    description: '',
    contact: {
      firstName: '',
      lastName: '',
      email: '',
    },
    license: {
      typeId: data?.licenseTypes[0].id,
      version: '',
      start: undefined,
      end: undefined,
      seats: 1,
      fee: {
        amount: 0,
        currency: 'EUR',
      }
    }
  };
  const validation = Yup.object().shape({
    name: Yup.string().required('Required'),
    country: Yup.string().required('Required').max(2, 'Must be 2-letter country code'),
    description: Yup.string(),
    contact: Yup.object({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().email().required('Required'),
    }),
    license: Yup.object().shape({
      typeId: Yup.number().required('Required'),
      version: Yup.string().required('Required'),
      start: Yup.date().required('Required'),
      end: Yup.date().required('Required').when('start', (start: Date) => {
        return start && Yup.date().min(start, 'Must be after start date');
      }),
      seats: Yup.number().required('Required').min(1, 'Must have at least 1 seat'),
      fee: Yup.object().shape({
        amount: Yup.number().required('Required').min(0.01, 'Must be positive'),
        currency: Yup.string().required('Required').max(3, 'Must be 3-letter currency code'),
      })
    })
  });

  return (
    <Formik initialValues={initialValues} validationSchema={validation} onSubmit={onFormSubmit}>
      {({isSubmitting}) => (
        <FormikForm>
          <FormSection>
            <FormField id="name" label="Name">
              <UIInput name="name" id="name" className="w-full"/>
            </FormField>
            <FormField id="country" label="Country">
              <UISelect name="country" options={Countries.getSorted().filter((x) => x[0] !== '00').map((x) => ({
                value: x[0],
                label: x[1]
              }))} className={"w-full"}
                        id="country"/>
            </FormField>
            <FormField id="description" label="About">
              <UIWysiwig name="description" id="description" minHeight={150}/>
            </FormField>
          </FormSection>
          <FormSection title="Contact">
            <FormField id="contact.firstName" label="First name">
              <UIInput name="contact.firstName" id="contact.firstName" className="w-full"/>
            </FormField>
            <FormField id="contact.lastName" label="Last name">
              <UIInput name="contact.lastName" id="contact.lastName" className="w-full"/>
            </FormField>
            <FormField id="contact.email" label="Email">
              <UIInput name="contact.email" id="contact.email" className="w-full"/>
            </FormField>
          </FormSection>
          <FormSection title="License">
            <FormField id="license.typeId" label="License type">
              <UISelect name="license.typeId" options={data?.licenseTypes.map(x => ({
                value: x.id,
                label: x.name,
              })).sort((a, b) => a.label.localeCompare(b.label)) || []} className="w-full"
                        id="license.typeId"/>
            </FormField>
            <FormField id="license.version" label="Version">
              <UIInput name="license.version" id="license.version" className="w-full"/>
            </FormField>
            <FormField id="license.period" label="Period">
              <UIDatePeriod name={["license.start", 'license.end']} id={"license.period"}/>
            </FormField>
            <FormField id="license.seats" label="Number of seats">
              <UIInput name="license.seats" id="license.seats" className="w-full" type="number"/>
            </FormField>
            <FormField id="license.fee" label="Fee">
              <UIPrice name={["license.fee.currency", "license.fee.amount"]} currencies={['eur', 'usd', 'gbp']}
                       id="license.fee"/>
            </FormField>
          </FormSection>
          <div className="pb-28">
            <div className="grid grid-cols-6 gap-4 items-start">
              <div className="col-span-3">
                <PrimaryButton loading={isSubmitting}> Save changes</PrimaryButton>
                <TextButton onClick={() => window.location.assign(backLink)}>Cancel</TextButton>
              </div>
            </div>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}


interface FormDataQuery {
  licenseTypes: Pick<LicenseType, "id" | "name">[]
}

const query = gql`
  query AddFormQuery {
    licenseTypes {
      id
      name
    }
  }
`;
